/**
 * Tier 7 intersection routes — every (industry × city × service) combination
 * we publish a dedicated landing page for.
 *
 * Per the SEO spec: 9 industries × 4 cities × SEO = 36 pages, plus
 * 9 industries × 8 cities × Web Design = 72 pages.
 * Each entry powers one route file under src/routes/{slug}.tsx using the
 * shared IntersectionPage template.
 */

import type { IntersectionPageConfig } from "@/components/IntersectionPage";

type IndustryDef = {
  key: string;
  display: string;
  industryLower: string;
  slugPiece: string;
  hubTo: string;
  hubLabel: string;
  seoSpokeTo: string;
  seoSpokeLabel: string;
  webDesignSpokeTo: string;
  webDesignSpokeLabel: string;
};

type CityDef = {
  key: string;
  display: string;
  slugPiece: string;
  hubTo: string;
  hubLabel: string;
  /** Whether this city is in the core SEO matrix (Tier 1). */
  coreSeo: boolean;
  /** Whether to auto-include this city in the full web-design matrix.
   *  Defaults to true for backwards compat; set false for cities added
   *  for SEO intersections only. */
  webDesignMatrix?: boolean;
};


const INDUSTRIES: IndustryDef[] = [
  {
    key: "hvac",
    display: "HVAC",
    industryLower: "hvac",
    slugPiece: "hvac",
    hubTo: "/hvac-marketing-colorado",
    hubLabel: "HVAC marketing Colorado",
    seoSpokeTo: "/hvac-seo-colorado",
    seoSpokeLabel: "HVAC SEO in Colorado",
    webDesignSpokeTo: "/hvac-web-design-colorado",
    webDesignSpokeLabel: "HVAC web design Colorado",
  },
  {
    key: "plumbing",
    display: "Plumbing",
    industryLower: "plumbing",
    slugPiece: "plumbing",
    hubTo: "/plumbing-marketing-colorado",
    hubLabel: "Plumbing marketing Colorado",
    seoSpokeTo: "/plumbing-seo-colorado",
    seoSpokeLabel: "Plumbing SEO in Colorado",
    webDesignSpokeTo: "/plumbing-web-design-colorado",
    webDesignSpokeLabel: "Plumbing web design Colorado",
  },
  {
    key: "roofing",
    display: "Roofing",
    industryLower: "roofing",
    slugPiece: "roofing",
    hubTo: "/roofing-seo-colorado",
    hubLabel: "Roofing SEO Colorado",
    seoSpokeTo: "/roofing-seo-colorado",
    seoSpokeLabel: "Roofing SEO Colorado (statewide)",
    webDesignSpokeTo: "/roofing-web-design-colorado",
    webDesignSpokeLabel: "Roofing web design Colorado",
  },
  {
    key: "electrical",
    display: "Electrical",
    industryLower: "electrical",
    slugPiece: "electrical",
    hubTo: "/electrical-contractor-marketing-colorado",
    hubLabel: "Electrical contractor marketing Colorado",
    seoSpokeTo: "/electrical-seo-colorado",
    seoSpokeLabel: "Electrical SEO in Colorado",
    // No dedicated electrical-web-design statewide page yet — fall back to industry hub.
    webDesignSpokeTo: "/electrical-contractor-marketing-colorado",
    webDesignSpokeLabel: "Electrical contractor marketing Colorado",
  },
  {
    key: "landscaping",
    display: "Landscaping",
    industryLower: "landscaping",
    slugPiece: "landscaping",
    hubTo: "/landscaping-seo-colorado",
    hubLabel: "Landscaping SEO Colorado",
    seoSpokeTo: "/landscaping-seo-colorado",
    seoSpokeLabel: "Landscaping SEO Colorado (statewide)",
    webDesignSpokeTo: "/landscaping-seo-colorado",
    webDesignSpokeLabel: "Landscaping marketing Colorado",
  },
  {
    key: "contractor",
    display: "Contractor",
    industryLower: "contractor",
    slugPiece: "contractor",
    hubTo: "/contractor-web-design-colorado",
    hubLabel: "Contractor web design Colorado",
    seoSpokeTo: "/contractor-web-design-colorado",
    seoSpokeLabel: "Contractor marketing Colorado",
    webDesignSpokeTo: "/contractor-web-design-colorado",
    webDesignSpokeLabel: "Contractor web design Colorado",
  },
  {
    key: "law-firm",
    display: "Law Firm",
    industryLower: "law firm",
    slugPiece: "law-firm",
    hubTo: "/law-firm-seo-colorado",
    hubLabel: "Law firm SEO Colorado",
    seoSpokeTo: "/law-firm-seo-colorado",
    seoSpokeLabel: "Law firm SEO Colorado (statewide)",
    webDesignSpokeTo: "/law-firm-seo-colorado",
    webDesignSpokeLabel: "Law firm marketing Colorado",
  },
  {
    key: "dental",
    display: "Dental",
    industryLower: "dental",
    slugPiece: "dental",
    hubTo: "/dental-marketing-colorado",
    hubLabel: "Dental marketing Colorado",
    seoSpokeTo: "/dental-seo-colorado",
    seoSpokeLabel: "Dental SEO in Colorado",
    webDesignSpokeTo: "/dental-marketing-colorado",
    webDesignSpokeLabel: "Dental marketing Colorado",
  },
  {
    key: "medical",
    display: "Medical",
    industryLower: "medical practice",
    slugPiece: "medical",
    hubTo: "/medical-marketing-colorado",
    hubLabel: "Medical marketing Colorado",
    seoSpokeTo: "/medical-seo-colorado",
    seoSpokeLabel: "Medical SEO in Colorado",
    webDesignSpokeTo: "/medical-marketing-colorado",
    webDesignSpokeLabel: "Medical marketing Colorado",
  },
];

const CITIES: CityDef[] = [
  {
    key: "colorado-springs",
    display: "Colorado Springs",
    slugPiece: "colorado-springs",
    hubTo: "/digital-marketing",
    hubLabel: "Colorado Springs digital marketing agency",
    coreSeo: true,
  },
  {
    key: "denver",
    display: "Denver",
    slugPiece: "denver",
    hubTo: "/denver-digital-marketing",
    hubLabel: "Denver digital marketing",
    coreSeo: true,
  },
  {
    key: "fort-collins",
    display: "Fort Collins",
    slugPiece: "fort-collins",
    hubTo: "/fort-collins-digital-marketing",
    hubLabel: "Fort Collins digital marketing",
    coreSeo: true,
  },
  {
    key: "boulder",
    display: "Boulder",
    slugPiece: "boulder",
    hubTo: "/boulder-digital-marketing",
    hubLabel: "Boulder digital marketing",
    coreSeo: true,
  },
  // Tier-2 — web-design matrix only (no SEO intersections to avoid
  // cannibalizing existing city-SEO pages).
  {
    key: "parker",
    display: "Parker",
    slugPiece: "parker",
    hubTo: "/parker-digital-marketing",
    hubLabel: "Parker digital marketing",
    coreSeo: false,
  },
  {
    key: "castle-rock",
    display: "Castle Rock",
    slugPiece: "castle-rock",
    hubTo: "/castle-rock-digital-marketing",
    hubLabel: "Castle Rock digital marketing",
    coreSeo: false,
  },
  {
    key: "lakewood",
    display: "Lakewood",
    slugPiece: "lakewood",
    hubTo: "/web-design-lakewood-colorado",
    hubLabel: "Lakewood web design",
    coreSeo: false,
  },
  {
    key: "pueblo",
    display: "Pueblo",
    slugPiece: "pueblo",
    hubTo: "/pueblo-digital-marketing",
    hubLabel: "Pueblo digital marketing",
    coreSeo: false,
  },
  {
    key: "monument",
    display: "Monument",
    slugPiece: "monument",
    hubTo: "/monument-seo",
    hubLabel: "Monument SEO",
    coreSeo: false,
    webDesignMatrix: false,
  },
  {
    key: "aurora",
    display: "Aurora",
    slugPiece: "aurora",
    hubTo: "/aurora-digital-marketing",
    hubLabel: "Aurora digital marketing",
    coreSeo: false,
    webDesignMatrix: false,
  },
  {
    key: "westminster",
    display: "Westminster",
    slugPiece: "westminster",
    hubTo: "/westminster-seo",
    hubLabel: "Westminster SEO",
    coreSeo: false,
    webDesignMatrix: false,
  },
  {
    key: "thornton",
    display: "Thornton",
    slugPiece: "thornton",
    hubTo: "/thornton-seo",
    hubLabel: "Thornton SEO",
    coreSeo: false,
    webDesignMatrix: false,
  },
  {
    key: "lone-tree",
    display: "Lone Tree",
    slugPiece: "lone-tree",
    hubTo: "/lone-tree-digital-marketing",
    hubLabel: "Lone Tree digital marketing",
    coreSeo: false,
    webDesignMatrix: false,
  },
  {
    key: "greenwood-village",
    display: "Greenwood Village",
    slugPiece: "greenwood-village",
    hubTo: "/greenwood-village-digital-marketing",
    hubLabel: "Greenwood Village digital marketing",
    coreSeo: false,
    webDesignMatrix: false,
  },
  {
    key: "highlands-ranch",
    display: "Highlands Ranch",
    slugPiece: "highlands-ranch",
    hubTo: "/highlands-ranch-digital-marketing",
    hubLabel: "Highlands Ranch digital marketing",
    coreSeo: false,
    webDesignMatrix: false,
  },
  {
    key: "centennial",
    display: "Centennial",
    slugPiece: "centennial",
    hubTo: "/centennial-seo",
    hubLabel: "Centennial SEO",
    coreSeo: false,
    webDesignMatrix: false,
  },
  {
    key: "manitou-springs",
    display: "Manitou Springs",
    slugPiece: "manitou-springs",
    hubTo: "/manitou-springs",
    hubLabel: "Manitou Springs SEO",
    coreSeo: false,
    webDesignMatrix: false,
  },
  {
    key: "security-widefield",
    display: "Security-Widefield",
    slugPiece: "security-widefield",
    hubTo: "/security-widefield",
    hubLabel: "Security-Widefield SEO",
    coreSeo: false,
    webDesignMatrix: false,
  },
];




type ServiceDef = {
  key: "seo" | "web-design";
  display: string;
  lower: string;
  hubTo: string;
  hubLabel: string;
  /** "{industry} {slugFragment} {city}" — produces the route slug. */
  slugFragment: string;
};

const SEO_SERVICE: ServiceDef = {
  key: "seo",
  display: "SEO",
  lower: "SEO",
  hubTo: "/colorado-springs-seo",
  hubLabel: "Colorado SEO services",
  slugFragment: "seo",
};

const WEB_DESIGN_SERVICE: ServiceDef = {
  key: "web-design",
  display: "Web Design",
  lower: "web design",
  hubTo: "/web-design",
  hubLabel: "Colorado web design services",
  slugFragment: "web-design",
};

function buildConfig(
  ind: IndustryDef,
  city: CityDef,
  svc: ServiceDef,
): IntersectionPageConfig {
  const slug = `${ind.slugPiece}-${svc.slugFragment}-${city.slugPiece}`;
  const isWebDesign = svc.key === "web-design";
  const metaTitle = isWebDesign
    ? `${ind.display} Web Design in ${city.display}, CO | Lead-Focused Websites, SEO & Google Maps for ${ind.display} Companies | Colorado Web Impressions`
    : `${ind.display} SEO in ${city.display}, CO | Local SEO, Google Maps & Lead Generation for ${ind.display} Companies | Colorado Web Impressions`;

  const metaDescription = isWebDesign
    ? `${ind.display} web design in ${city.display}, Colorado. Fast, mobile-first, lead-focused sites. Owner-led, month-to-month. Call (719) 419-3935 for a free review.`
    : `Local SEO for ${ind.display} companies in ${city.display}, Colorado. Owner-led, month-to-month, real local rankings. Call (719) 419-3935 for a free audit.`;
  const spokeTo = isWebDesign ? ind.webDesignSpokeTo : ind.seoSpokeTo;
  const spokeLabel = isWebDesign ? ind.webDesignSpokeLabel : ind.seoSpokeLabel;
  return {
    slug,
    industry: ind.display,
    industryLower: ind.industryLower,
    city: city.display,
    service: svc.display,
    serviceLower: svc.lower,
    industryHubTo: ind.hubTo,
    industryHubLabel: ind.hubLabel,
    cityHubTo: city.hubTo,
    cityHubLabel: city.hubLabel,
    serviceHubTo: svc.hubTo,
    serviceHubLabel: svc.hubLabel,
    industrySpokeTo: spokeTo,
    industrySpokeLabel: spokeLabel,
    metaTitle,
    metaDescription,
  };
}

/**
 * Tier-1 satellite-city industry SEO additions (Local-Dominance pivot).
 * Monument, Castle Rock, and Parker each get a focused SEO matrix
 * for the industries with real local search volume in that town.
 * These are the city-by-city extras the SEO matrix above does NOT
 * auto-generate (because those cities have coreSeo=false).
 */
const TIER1_SEO_PAIRS: Array<[industryKey: string, cityKey: string]> = [
  // Monument — Tri-Lakes, residential-heavy, dentist + real-estate buyer base
  ["hvac", "monument"],
  ["plumbing", "monument"],
  ["electrical", "monument"],
  ["roofing", "monument"],
  ["dental", "monument"],
  ["landscaping", "monument"],
  // Castle Rock — fast-growth Douglas County, trades + landscaping heavy
  ["hvac", "castle-rock"],
  ["plumbing", "castle-rock"],
  ["electrical", "castle-rock"],
  ["roofing", "castle-rock"],
  ["landscaping", "castle-rock"],
  ["contractor", "castle-rock"],
  ["law-firm", "castle-rock"],
  // Parker — dense suburban, dentist + med-services + trades
  ["hvac", "parker"],
  ["plumbing", "parker"],
  ["electrical", "parker"],
  ["roofing", "parker"],
  ["dental", "parker"],
  ["medical", "parker"],
  ["landscaping", "parker"],
  // Aurora — home-services + legal
  ["hvac", "aurora"],
  ["plumbing", "aurora"],
  ["roofing", "aurora"],
  ["electrical", "aurora"],
  ["landscaping", "aurora"],
  ["law-firm", "aurora"],
  // Lakewood
  ["hvac", "lakewood"],
  ["plumbing", "lakewood"],
  ["roofing", "lakewood"],
  ["electrical", "lakewood"],
  ["landscaping", "lakewood"],
  ["law-firm", "lakewood"],
  // Westminster
  ["hvac", "westminster"],
  ["plumbing", "westminster"],
  ["roofing", "westminster"],
  ["electrical", "westminster"],
  ["landscaping", "westminster"],
  ["law-firm", "westminster"],
  // Thornton
  ["hvac", "thornton"],
  ["plumbing", "thornton"],
  ["roofing", "thornton"],
  ["electrical", "thornton"],
  ["landscaping", "thornton"],
  ["law-firm", "thornton"],
  // Pueblo — home-services dominant
  ["hvac", "pueblo"],
  ["plumbing", "pueblo"],
  ["roofing", "pueblo"],
  ["electrical", "pueblo"],
  ["landscaping", "pueblo"],
  // Lone Tree — DTC corridor, wealthy residential + professional services
  ["hvac", "lone-tree"],
  ["plumbing", "lone-tree"],
  ["roofing", "lone-tree"],
  ["electrical", "lone-tree"],
  ["landscaping", "lone-tree"],
  ["law-firm", "lone-tree"],
  ["dental", "lone-tree"],
  ["medical", "lone-tree"],
  // Greenwood Village — DTC corporate, law/finance dense
  ["hvac", "greenwood-village"],
  ["plumbing", "greenwood-village"],
  ["roofing", "greenwood-village"],
  ["electrical", "greenwood-village"],
  ["landscaping", "greenwood-village"],
  ["law-firm", "greenwood-village"],
  // Highlands Ranch — large residential, all home services
  ["hvac", "highlands-ranch"],
  ["plumbing", "highlands-ranch"],
  ["roofing", "highlands-ranch"],
  ["electrical", "highlands-ranch"],
  ["landscaping", "highlands-ranch"],
  ["law-firm", "highlands-ranch"],
  ["dental", "highlands-ranch"],
  ["medical", "highlands-ranch"],
  // Centennial — Arapahoe wealth corridor
  ["hvac", "centennial"],
  ["plumbing", "centennial"],
  ["roofing", "centennial"],
  ["electrical", "centennial"],
  ["landscaping", "centennial"],
  ["law-firm", "centennial"],
  // Manitou Springs — tourism + local service
  ["hvac", "manitou-springs"],
  ["plumbing", "manitou-springs"],
  ["roofing", "manitou-springs"],
  ["electrical", "manitou-springs"],
  ["landscaping", "manitou-springs"],
  // Security-Widefield — south Springs suburb
  ["hvac", "security-widefield"],
  ["plumbing", "security-widefield"],
  ["roofing", "security-widefield"],
  ["electrical", "security-widefield"],
  ["landscaping", "security-widefield"],
];



/** All intersection configs (industry × city × service). */
export const INTERSECTION_CONFIGS: IntersectionPageConfig[] = [
  // SEO matrix — core 4 cities only (existing).
  ...INDUSTRIES.flatMap((ind) =>
    CITIES.filter((c) => c.coreSeo).map((city) =>
      buildConfig(ind, city, SEO_SERVICE),
    ),
  ),
  // Web-design matrix — all 8 cities.
  ...INDUSTRIES.flatMap((ind) =>
    CITIES.filter((c) => c.webDesignMatrix !== false).map((city) => buildConfig(ind, city, WEB_DESIGN_SERVICE)),

  ),
  // Tier-1 satellite SEO additions (selective pairs, not full matrix).
  ...TIER1_SEO_PAIRS.flatMap(([indKey, cityKey]) => {
    const ind = INDUSTRIES.find((i) => i.key === indKey);
    const city = CITIES.find((c) => c.key === cityKey);
    if (!ind || !city) return [];
    return [buildConfig(ind, city, SEO_SERVICE)];
  }),
];

/** Just the slugs — used by the sitemap. */
export const INTERSECTION_SLUGS: string[] = INTERSECTION_CONFIGS.map(
  (c) => c.slug,
);

